/**
 * Per-agent credential rows. One row per agent referenced by any config;
 * the row shape depends on whether the config uses ``api_key`` (single-line
 * password input) or ``subscription`` (textarea for the pasted JSON blob,
 * or single-line input for Claude Code's OAuth token).
 *
 * BOTH agents get an in-app connect flow, so nobody needs a terminal — each
 * on the OAuth path its CLI actually sanctions (see AgentConnectSection):
 *
 *   Claude Code — `claude setup-token`: we open the claude.ai authorize URL
 *   and the user pastes back the code claude.ai shows.
 *
 *   Codex — `codex login --device-auth`: we open OpenAI's device page and show
 *   a one-time code the user enters there; nothing comes back to us. (Codex's
 *   default login is a localhost:1455 loopback flow, which a server cannot
 *   complete — the redirect would land on the backend host.)
 *
 * The manual paste input stays available as a fallback for both.
 */
import { useState } from 'react'
import { AGENTS, type AgentId } from '@/data/llmCatalog'
import {
  testAgent,
  startClaudeOauth,
  completeClaudeOauth,
  cancelClaudeOauth,
  getClaudeOauthStatus,
  disconnectClaudeOauth,
  startCodexOauth,
  pollCodexOauth,
  cancelCodexOauth,
  getCodexOauthStatus,
  disconnectCodexOauth,
} from '@/api/settings'
import type { AgentCredentials, LlmScope } from '@/types'
import { CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react'
import AgentConnectSection, { type AgentConnectApi } from './AgentConnectSection'
import toast from 'react-hot-toast'

interface Props {
  /** One entry per (agent, auth) combination referenced by some config. */
  needs: { agent: AgentId; auth: 'api_key' | 'subscription' | 'configured' }[]
  /** Masked: server returns booleans showing which creds are set. */
  masked: Partial<Record<keyof AgentCredentials, boolean>>
  draft: AgentCredentials
  onChange: (next: AgentCredentials) => void
  /** Scope the OAuth connect flow stores into (mirrors the panel's scope toggle). */
  scope: LlmScope
  /** Notifies the parent when the connect/disconnect flow changed a stored
   * credential server-side, so the masked state can be refreshed without a
   * full reload (which would clobber unsaved edits). */
  onCredentialStored: (field: keyof AgentCredentials, isSet: boolean) => void
}

// OpenCode has no entry: it never holds a Twasta subscription credential (it
// runs on OpenCode's own configured providers, or a stored provider API key).
export const AGENT_CRED_FIELD: Partial<Record<AgentId, keyof AgentCredentials>> = {
  claude_code: 'claude_oauth_token',
  codex: 'codex_oauth_json',
}

export default function AgentCredentialPanel({ needs, masked, draft, onChange, scope, onCredentialStored }: Props) {
  // De-duplicate: an agent only needs ONE credential entry total (the field
  // depends on auth mode but the same column is reused for api_key mode
  // where the credential lives in the matching PROVIDER key column instead).
  // For api_key mode the agent uses the provider's key — see settings_service
  // resolve_for_task — so this panel only needs to render rows for the
  // *subscription* needs.
  const subsAgents = Array.from(new Set(
    needs.filter((n) => n.auth === 'subscription' && AGENT_CRED_FIELD[n.agent]).map((n) => n.agent),
  ))

  if (subsAgents.length === 0) {
    return (
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
        No agent configurations use subscription authentication. Agent
        configs in “API key” mode reuse the provider's key from the panel
        above.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {subsAgents.map((a) => {
        const spec = AGENTS.find((x) => x.id === a)!
        const field = AGENT_CRED_FIELD[a]!
        return (
          <CredRow
            key={a}
            agent={a}
            label={spec.label}
            usesOauthEnv={spec.oauth_env_native}
            isSet={!!masked[field]}
            value={draft[field] || ''}
            onChange={(v) => onChange({ ...draft, [field]: v })}
            scope={scope}
            onStored={(isSet) => onCredentialStored(field, isSet)}
          />
        )
      })}
    </div>
  )
}

function CredRow({
  agent,
  label,
  usesOauthEnv,
  isSet,
  value,
  onChange,
  scope,
  onStored,
}: {
  agent: AgentId
  label: string
  usesOauthEnv: boolean
  isSet: boolean
  value: string
  onChange: (v: string) => void
  scope: LlmScope
  onStored: (isSet: boolean) => void
}) {
  const [reveal, setReveal] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; error: string | null } | null>(null)
  // Both agents have an in-app connect flow, which makes manual paste the
  // fallback — collapsed until the user asks for it (or has typed into it).
  const isClaude = agent === 'claude_code'
  const [showManual, setShowManual] = useState(false)

  async function handleTest() {
    if (!value && !isSet) {
      toast.error('Enter a credential to test')
      return
    }
    // Blank + stored: the backend tests the stored credential for the scope.
    setTesting(true)
    setTestResult(null)
    try {
      const result = await testAgent(agent, 'subscription', value, scope)
      setTestResult(result)
      if (result.ok) toast.success(`${label} credential works`)
      else toast.error(result.error || 'Credential test failed')
    } catch (e: any) {
      const err = e?.response?.data?.detail || e?.message || 'Test failed'
      setTestResult({ ok: false, error: err })
      toast.error(err)
    } finally {
      setTesting(false)
    }
  }

  // Claude Code uses a short OAuth token in a single line; Codex uses a
  // multi-line JSON blob (the contents of ~/.codex/auth.json).
  const isMultiline = !usesOauthEnv
  const providerName = isClaude ? 'Claude' : 'Codex'
  const helperText = isClaude
    ? 'Connect your Claude subscription below, or paste a token from `claude setup-token` manually. The token is long-lived (about a year).'
    : 'Connect your ChatGPT subscription below — you sign in with a one-time code on OpenAI\'s page. Or paste the contents of ~/.codex/auth.json from a machine where you ran `codex login`.'

  // Each agent's connect flow, on the OAuth path its CLI sanctions.
  const connectApi: AgentConnectApi = isClaude
    ? {
        kind: 'paste',
        start: startClaudeOauth,
        complete: (flowId, code) => completeClaudeOauth(flowId, code, scope),
        cancel: cancelClaudeOauth,
        status: () => getClaudeOauthStatus(scope),
        disconnect: () => disconnectClaudeOauth(scope),
      }
    : {
        kind: 'device',
        start: startCodexOauth,
        poll: (flowId) => pollCodexOauth(flowId, scope),
        cancel: cancelCodexOauth,
        status: () => getCodexOauthStatus(scope),
        disconnect: () => disconnectCodexOauth(scope),
      }

  const manualVisible = showManual || !!value

  return (
    <div
      style={{
        padding: '12px 14px',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        background: 'var(--color-bg-elevated)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{label} — subscription credential</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
            {helperText}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {!isMultiline && manualVisible && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setReveal((r) => !r)}
              title={reveal ? 'Hide' : 'Reveal'}
              style={{ padding: '4px 8px' }}
            >
              {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleTest}
            disabled={testing}
            style={{ minWidth: 70 }}
          >
            {testing ? 'Testing…' : 'Test'}
          </button>
        </div>
      </div>

      <AgentConnectSection
        provider={providerName}
        isSet={isSet}
        statusKey={scope}
        onStored={onStored}
        api={connectApi}
        footer={
          <>
            The connection is saved to the <strong>{scope === 'tenant' ? 'tenant' : 'personal'}</strong> scope
            immediately — no separate Save needed. Usage bills to the connected {providerName} plan.
          </>
        }
      />

      {!manualVisible && (
        <button
          type="button"
          onClick={() => setShowManual(true)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            marginTop: 8,
            fontSize: 13,
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          {isClaude ? 'Paste a token manually instead' : 'Paste an auth.json manually instead'}
        </button>
      )}

      {manualVisible && (
        isMultiline ? (
          <textarea
            className="input"
            rows={5}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={isSet ? '«JSON credentials already stored — paste a new blob to replace»' : '{ "auth_mode": "chatgpt", "tokens": { ... } }'}
            style={{ marginTop: 8, fontFamily: 'var(--font-mono, monospace)', fontSize: 13, resize: 'vertical' }}
          />
        ) : (
          <input
            type={reveal ? 'text' : 'password'}
            className="input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={isSet ? '••••••••  (token already stored — leave blank to keep)' : 'sk-ant-…'}
            style={{ marginTop: 8 }}
          />
        )
      )}
      {testResult && (
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: testResult.ok ? 'var(--color-success, #16a34a)' : 'var(--color-danger, #dc2626)',
          }}
        >
          {testResult.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
          {testResult.ok ? 'Credential validated.' : testResult.error}
        </div>
      )}
    </div>
  )
}
